import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { HERO, COMMITMENTS, VESSELS, COMPANY } from "../data/site";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative w-full h-[100svh] min-h-[640px] overflow-hidden"
      >
        <img
          src={HERO.image}
          alt={HERO.imageAlt}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-scrim" />
        <div className="absolute inset-0 flex flex-col">
          <div className="flex-1" />
          <div className="max-w-[1600px] mx-auto w-full px-6 md:px-10 lg:px-14 pb-20 md:pb-28">
            <div className="max-w-3xl">
              <p
                className="overline text-[#f7f4ec]/85 rise-in"
                data-testid="home-hero-eyebrow"
              >
                Bozeman · Montana
              </p>
              <h1
                data-testid="home-hero-headline"
                className="font-serif text-white text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight mt-6 rise-in rise-delay-1"
              >
                {HERO.headline}
              </h1>
              <hr className="accent-rule mt-6 rise-in rise-delay-1" />
              <p
                data-testid="home-hero-line"
                className="text-[#f7f4ec] text-lg md:text-2xl mt-8 max-w-2xl font-light leading-relaxed rise-in rise-delay-2"
              >
                {HERO.line}
              </p>
              <div className="mt-12 rise-in rise-delay-3">
                <Link
                  to="/book"
                  data-testid="home-hero-book-now"
                  className="btn-primary"
                >
                  Book Now
                  <ArrowRight size={16} className="ml-3" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MONTANA DNA + SUPPORTING COMMITMENTS */}
      <section
        data-testid="home-commitments"
        className="bg-[#f7f4ec] py-20 md:py-28"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-7">
            <p className="overline" data-testid="home-dna-eyebrow">
              The Foundation
            </p>
            <h2
              className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#13263c] mt-4 leading-[0.98] tracking-tight"
              data-testid="home-dna-title"
            >
              {COMMITMENTS.primary.title}.
            </h2>
            <hr className="accent-rule mt-5" />
            <p
              className="mt-8 text-xl md:text-2xl text-[#2b2b2b] max-w-xl font-light leading-relaxed"
              data-testid="home-dna-body"
            >
              {COMMITMENTS.primary.body}
            </p>
          </div>
          <div className="md:col-span-5 md:pl-12 md:border-l md:border-[#e3ddcf] flex flex-col gap-10">
            {COMMITMENTS.secondary.map((c, i) => (
              <div key={c.title} data-testid={`home-commitment-${i}`}>
                <h3 className="font-serif text-2xl md:text-3xl text-[#13263c]">
                  {c.title}
                </h3>
                <p className="mt-3 text-[#4a4a4a] leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE FLEET preview — section header w/ side rules */}
      <section
        data-testid="home-fleet"
        className="bg-[#efe9da] py-20 md:py-28"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="flex items-center justify-center gap-6 md:gap-10">
            <hr className="flex-1 hairline" />
            <div className="text-center">
              <h2 className="font-serif text-4xl md:text-5xl text-[#13263c] tracking-tight">Our Fleet</h2>
              <p className="mt-2 text-[#4a4a4a]">Quality, reliable vessels for your day on the water.</p>
            </div>
            <hr className="flex-1 hairline" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-7 mt-12 md:mt-14">
            {VESSELS.map((v, i) => (
              <Link
                key={v.slug}
                to={`/fleet/${v.slug}`}
                data-testid={`home-vessel-${v.slug}`}
                className="surface-card rounded-md overflow-hidden block group"
                style={{ animationDelay: `${0.06 * i}s` }}
              >
                <div className="img-frame aspect-[4/3]">
                  <img src={v.image} alt={v.name} loading="lazy" />
                </div>
                <div className="p-5">
                  <h3 className="font-serif text-xl md:text-2xl text-[#13263c] group-hover:text-[#1a3a5c] transition-colors leading-tight">
                    {v.shortName}
                  </h3>
                  <p className="mt-1.5 text-[#4a4a4a] text-sm">{v.line}</p>
                  <hr className="accent-rule mt-4" />
                  <p className="mt-3 text-[#13263c] text-sm">{v.rate}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10 md:mt-14">
            <Link to="/fleet" data-testid="home-fleet-view-all" className="link-underline">
              View all vessels →
            </Link>
          </div>
        </div>
      </section>

      {/* QUIET CTA STRIP */}
      <section
        data-testid="home-cta-strip"
        className="bg-[#f7f4ec] py-20 md:py-28"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <h2 className="font-serif text-4xl md:text-5xl text-[#13263c] leading-tight tracking-tight">
              An easier day, from put-in to take-out.
            </h2>
            <hr className="accent-rule mt-4" />
            <p className="mt-5 text-[#4a4a4a] max-w-xl leading-relaxed">
              Reservations open soon. Reach us directly at{" "}
              <a className="link-underline" href={COMPANY.phoneHref} data-testid="home-cta-phone">
                {COMPANY.phone}
              </a>{" "}or{" "}
              <a className="link-underline" href={COMPANY.emailHref} data-testid="home-cta-email">
                {COMPANY.email}
              </a>.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/book" data-testid="home-cta-book-now" className="btn-primary">
              Book Now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
